import { useState, useRef, useEffect, useCallback } from 'preact/hooks';

/** A Range + the scroll-target element for that match. */
interface Match {
  range: Range;
  anchor: Element;
}

/** Find all text ranges matching `query` (case-insensitive) inside `root`. */
function findRanges(root: HTMLElement, query: string): Match[] {
  if (!query) return [];
  const q = query.toLowerCase();
  const matches: Match[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode() as Text | null;
  while (node) {
    const text = node.textContent || '';
    const lc = text.toLowerCase();
    let idx = lc.indexOf(q);
    while (idx !== -1) {
      const range = document.createRange();
      range.setStart(node, idx);
      range.setEnd(node, idx + query.length);
      const anchor = (node.parentElement as Element) ?? root;
      matches.push({ range, anchor });
      idx = lc.indexOf(q, idx + 1);
    }
    node = walker.nextNode() as Text | null;
  }
  return matches;
}

/** Apply or clear CSS Custom Highlight API highlights. */
function applyHighlights(matches: Match[], currentIdx: number) {
  if (!('highlights' in CSS)) return;
  if (matches.length === 0) {
    CSS.highlights.delete('chat-find');
    CSS.highlights.delete('chat-find-current');
    return;
  }
  // All matches
  CSS.highlights.set('chat-find', new Highlight(...matches.map((m) => m.range)));
  // Current match
  const cur = matches[currentIdx];
  if (cur) {
    CSS.highlights.set('chat-find-current', new Highlight(cur.range));
  } else {
    CSS.highlights.delete('chat-find-current');
  }
}

/** Scroll the chat-area container so the current match is visible. */
function scrollToMatch(match: Match | undefined) {
  if (!match) return;
  match.anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

export interface UseChatFindReturn {
  isOpen: boolean;
  query: string;
  matchIndex: number;  // 0-based
  matchCount: number;
  openFind: () => void;
  closeFind: () => void;
  setQuery: (q: string) => void;
  goNext: () => void;
  goPrev: () => void;
}

export function useChatFind(): UseChatFindReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQueryState] = useState('');
  const [matchIndex, setMatchIndex] = useState(0);
  const matchesRef = useRef<Match[]>([]);
  const [matchCount, setMatchCount] = useState(0);

  /** Re-run search whenever query changes. */
  const runSearch = useCallback((q: string) => {
    const root = document.querySelector<HTMLElement>('.chat-area');
    if (!root || !q.trim()) {
      matchesRef.current = [];
      setMatchCount(0);
      setMatchIndex(0);
      applyHighlights([], 0);
      return;
    }
    const matches = findRanges(root, q.trim());
    matchesRef.current = matches;
    setMatchCount(matches.length);
    const idx = 0;
    setMatchIndex(idx);
    applyHighlights(matches, idx);
    scrollToMatch(matches[idx]);
  }, []);

  const setQuery = useCallback((q: string) => {
    setQueryState(q);
    runSearch(q);
  }, [runSearch]);

  const goNext = useCallback(() => {
    const count = matchesRef.current.length;
    if (!count) return;
    setMatchIndex((prev) => {
      const next = (prev + 1) % count;
      applyHighlights(matchesRef.current, next);
      scrollToMatch(matchesRef.current[next]);
      return next;
    });
  }, []);

  const goPrev = useCallback(() => {
    const count = matchesRef.current.length;
    if (!count) return;
    setMatchIndex((prev) => {
      const next = (prev - 1 + count) % count;
      applyHighlights(matchesRef.current, next);
      scrollToMatch(matchesRef.current[next]);
      return next;
    });
  }, []);

  const openFind = useCallback(() => {
    setIsOpen(true);
    // Ghost the pencil FAB so it doesn't poke through the find bar
    document.dispatchEvent(new CustomEvent('search-activated'));
  }, []);

  const closeFind = useCallback(() => {
    setIsOpen(false);
    document.dispatchEvent(new CustomEvent('search-deactivated'));
    setQueryState('');
    matchesRef.current = [];
    setMatchCount(0);
    setMatchIndex(0);
    applyHighlights([], 0);
  }, []);

  /** Re-apply highlights when messages change (DOM mutation after streaming). */
  useEffect(() => {
    if (!isOpen || !query.trim()) return;
    // Small debounce so we don't thrash during streaming
    const id = setTimeout(() => runSearch(query), 300);
    return () => clearTimeout(id);
  }, [isOpen, query, runSearch]);

  /** Clear highlights when closed. */
  useEffect(() => {
    if (!isOpen) applyHighlights([], 0);
  }, [isOpen]);

  return {
    isOpen,
    query,
    matchIndex,
    matchCount,
    openFind,
    closeFind,
    setQuery,
    goNext,
    goPrev,
  };
}
